import React, { useEffect, useMemo, useState } from "react";
import {
	Alert,
	Button,
	Card,
	Checkbox,
	Col,
	Input,
	Radio,
	Row,
	Select,
	Tag,
	Typography,
	Grid,
	message,
} from "antd";
import InputCpf from "components/atom/InputCpf";
import InputCep from "components/atom/InputCep";
import utils from "utils";
import { CopyOutlined } from "@ant-design/icons";
import InputCpfCnpj from "components/atom/InputCpfCnpj";
import InputTelefoneInternacional from "components/atom/InputTelefoneInternacional";
import {
	cupomSeAplicaAoPlano,
	obterDetalhesDescontoCupom,
	formatarPorcentagemCupom,
	resumoExibicaoCupom,
} from "utils/cupom";

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const onlyDigits = (v) => (v || "").replace(/\D/g, "");

const formatCurrency = (value) => {
	const safe = Number(value || 0);
	return `R$ ${safe.toFixed(2).replace(".", ",")}`;
};

const formatCardNumber = (value) => {
	const digits = onlyDigits(value).slice(0, 19);
	return digits.replace(/(\d{4})(?=\d)/g, "$1 ");
};

const formatExpiry = (value) => {
	const digits = onlyDigits(value).slice(0, 4);
	if (digits.length <= 2) return digits;
	return `${digits.slice(0, 2)}/${digits.slice(2)}`;
};

// Máscara 00000-000 (mesma do atom InputCep) — usada pra exibir o CEP
// inicial já formatado; validação e payload sempre re-strippam os dígitos.
const formatCep = (value) => {
	const digits = onlyDigits(value).slice(0, 8);
	if (digits.length <= 5) return digits;
	return `${digits.slice(0, 5)}-${digits.slice(5)}`;
};

// Rótulo de campo com o marcador de obrigatório (asterisco vermelho, padrão
// antd) — o form é manual (fora do antd Form), então o marcador também é.
const RotuloCampo = ({ obrigatorio = false, children }) => (
	<div style={{ fontWeight: 500, marginBottom: 6 }}>
		{obrigatorio && (
			<span style={{ color: "#ff4d4f", marginRight: 4 }}>*</span>
		)}
		{children}
	</div>
);

const parseExpiry = (value) => {
	const digits = onlyDigits(value);
	if (digits.length < 4) return { month: null, year: null };
	const month = digits.slice(0, 2);
	const year2 = digits.slice(2, 4);
	const year = `20${year2}`;
	return { month, year };
};

const calcularTotalComJuros = (
	valorFixo,
	valorVariavel,
	parcelas,
	taxasParcelamento = [],
	// Quando false, o variável NÃO compõe o total cobrado (à vista/parcelado) —
	// ele é recorrente-mensal e cobrado à parte (plano multi-mês, ex.: anual). O
	// default true preserva os fluxos em que a variável entra no valor cobrado
	// (mensal e faturas reais já emitidas com variável embutida).
	variavelNoTotal = true,
) => {
	const parcelasSeguras = Math.max(1, Math.min(21, parcelas || 1));
	const fixoCentavos = Math.round((Number(valorFixo) || 0) * 100);
	const variavelCentavos = Math.round((Number(valorVariavel) || 0) * 100);
	const baseCentavos = fixoCentavos;

	const faixas = Array.isArray(taxasParcelamento)
		? taxasParcelamento
		: taxasParcelamento?.faixas || [];
	const antecipacaoMensal =
		typeof taxasParcelamento?.antecipacao_mensal_percentual === "number"
			? taxasParcelamento.antecipacao_mensal_percentual
			: 0;
	const parcelaIntervaloMeses =
		typeof taxasParcelamento?.parcela_intervalo_meses === "number"
			? taxasParcelamento.parcela_intervalo_meses
			: 1;

	if (parcelasSeguras <= 1 || faixas.length === 0) {
		return {
			total: baseCentavos / 100,
			juros: 0,
			valorParcela: baseCentavos / 100,
		};
	}

	const faixa =
		faixas.find(
			(t) =>
				parcelasSeguras >= t.minParcelas &&
				parcelasSeguras <= t.maxParcelas,
		) || faixas[faixas.length - 1];

	const r = faixa?.percentual || 0;
	const f = (faixa?.fixaCentavos || 0) / 100;
	const a = antecipacaoMensal || 0;
	const delta = parcelaIntervaloMeses;
	const m = parcelasSeguras;
	const fator = ((1 + a) ** delta - 1) / (1 - (1 + a) ** (-m * delta));
	const p = ((baseCentavos / 100) * m * fator) / (1 - r) + f / (1 - r);
	const fixoComJurosCentavos = Math.ceil(p * 100);
	const variavelNoTotalCentavos = variavelNoTotal ? variavelCentavos : 0;
	const totalCentavos = fixoComJurosCentavos + variavelNoTotalCentavos;
	const jurosCentavos =
		totalCentavos - (fixoCentavos + variavelNoTotalCentavos);

	return {
		total: totalCentavos / 100,
		juros: jurosCentavos / 100,
		valorParcela: totalCentavos / 100 / parcelasSeguras,
	};
};

/**
 * Componente reutilizável de checkout (não faz requisições).
 * - Coleta dados do pagador + método de pagamento (cartão ou pix)
 * - Emite payload via onSubmit
 * - Exibe feedback (sucesso/erro/PIX QR) via props
 */
export default function CheckoutPagamento({
	planName,
	features = [],
	fixedValue = 0,
	variableMonthlyValue = 0,
	totalValue = 0,
	maxInstallments = 1,
	studentsCount = 0,
	taxasParcelamentoCartao = [],
	loading = false,
	errorMessage,
	result, // { status: 'PAID'|'PIX', pix?: { encodedImage, payload }, message? }
	onSubmit,
	submitLabel = "Confirmar assinatura",
	initialCardNumber = "",
	initialCardExpiry = "",
	initialCardCvc = "",
	initialCardHolderName = "",
	initialHolderEmail = "",
	initialHolderCpfCnpj = "",
	initialHolderPostalCode = "",
	initialHolderAddressNumber = "",
	initialHolderAddressComplement = "",
	initialHolderPhone = "",
	initialHolderMobilePhone = "",
	// Quando true, os campos Telefone/Celular usam o input internacional (com
	// bandeira de país), igual à tela de registro. Default false mantém os
	// inputs de texto simples nos demais fluxos (inclusive os em Modal).
	telefoneInternacional = false,
	hideFixedValueLine = false,
	// Funil v3: versão da cláusula de flip (oferta.clausula_flip_versao). Quando
	// presente, exige o checkbox de consentimento e envia no payload.
	consentimentoFlipVersao = null,
	// PR 9c: cobrança ÚNICA (pró-rata do flip) — resumo sem "/mês" e sem a linha
	// de valor variável (não é assinatura recorrente).
	cobrancaUnica = false,
	// Abatimento do mês atual (reais) = resíduo do plano atual já abatido na
	// troca-ciclo. Vira a linha "Abatimento do mês atual" no resumo e entra no
	// total/parcelamento. O back é autoritativo.
	creditoAbatido = 0,
	// Saldo de crédito da CONTA (reais) — abatido por último (depois de cupom e
	// resíduo). Vira a linha "Crédito disponível". O aplicado = min(saldo, o que
	// sobra). O back consome no pagamento; aqui é só preview.
	creditoConta = 0,
	// Cupom no checkout: só nos fluxos de COMPRA ativa (contratar, troca pro
	// anual, adiantar um ano). Quando true, mostra o campo "Tem um cupom?" no
	// resumo e — se houver cupom aplicável ao planoParaCupom — risca o fixo e
	// usa o valor descontado no total/parcelamento. O desconto incide SÓ no
	// fixo (o variável por aluno é custo unitário do produto).
	permiteCupom = false,
	cupomAtivo = null,
	planoParaCupom = null,
	onAplicarCupom,
	onRemoverCupom,
	aplicandoCupom = false,
	// Cartão SALVO do plano ({ ultimos_digitos, bandeira, titular } | null):
	// quando presente, o modo cartão começa com ele selecionado — o usuário só
	// confirma o pagamento (o back cobra pelo token), com a opção de trocar
	// pra um cartão novo. Só nos fluxos que aceitam `usarCartaoSalvo`.
	cartaoSalvo = null,
	// Se o valor variável compõe o total COBRADO (à vista/parcelado). Default
	// true (mensal e faturas reais já emitidas com variável embutida). No plano
	// recorrente longo (freq > 1, ex.: anual) a variável é cobrada MENSALMENTE à
	// parte: passar false pra ela NÃO entrar no total à vista nem no parcelado —
	// só aparece como a linha informativa "/mês" do resumo.
	variavelNoTotal = true,
}) {
	const [paymentMethod, setPaymentMethod] = useState("CREDIT_CARD"); // CREDIT_CARD | PIX
	// Pagar com o cartão salvo (default quando ele existe). Trocar → mostra o
	// form de cartão novo; dá pra voltar pro salvo sem perder o preenchido.
	const [usarCartaoSalvo, setUsarCartaoSalvo] = useState(!!cartaoSalvo);
	const [cupomCodeInput, setCupomCodeInput] = useState("");

	const [cardNumber, setCardNumber] = useState(initialCardNumber);
	const [cardExpiry, setCardExpiry] = useState(initialCardExpiry);
	const [cardCvc, setCardCvc] = useState(initialCardCvc);
	const [cardHolderName, setCardHolderName] = useState(initialCardHolderName);
	const [holderEmail, setHolderEmail] = useState(initialHolderEmail);
	const [holderCpfCnpj, setHolderCpfCnpj] = useState(initialHolderCpfCnpj);
	const [holderPostalCode, setHolderPostalCode] = useState(
		formatCep(initialHolderPostalCode),
	);
	const [holderAddressNumber, setHolderAddressNumber] = useState(
		initialHolderAddressNumber,
	);
	const [holderAddressComplement, setHolderAddressComplement] = useState(
		initialHolderAddressComplement,
	);
	const [holderPhone, setHolderPhone] = useState(initialHolderPhone);
	const [holderMobilePhone, setHolderMobilePhone] = useState(
		initialHolderMobilePhone,
	);
	const [saveCard, setSaveCard] = useState(true);

	// Pré-preenche o CPF do PIX com o documento já informado (titular/conta), pra
	// não pedir o CPF de novo no pagamento depois de o usuário já tê-lo digitado
	// (ex.: passo "confirme seus dados" do checkout-oferta). Só quando é um CPF
	// (11 dígitos) — CNPJ não vale como chave do pagador PIX.
	const [cpfPix, setCpfPix] = useState(() => {
		const doc = (initialHolderCpfCnpj || "").replace(/\D/g, "");
		return doc.length === 11 ? doc : "";
	});
	const [installments, setInstallments] = useState(1);
	const isMobile = !utils.getBreakPoint(useBreakpoint()).includes("lg");

	// Cupom (preview): o desconto incide só no FIXO; o variável segue intacto.
	// Quando há cupom aplicável ao planoParaCupom, o fixo descontado vira a base
	// do total e do parcelamento. Os números vêm prontos do back (utils/cupom só
	// lê), então o preview nunca diverge do que é cobrado.
	const cupomAplicado =
		permiteCupom &&
		!!cupomAtivo &&
		!!planoParaCupom &&
		cupomSeAplicaAoPlano(planoParaCupom, cupomAtivo);
	const detalheCupom = cupomAplicado
		? obterDetalhesDescontoCupom(planoParaCupom, cupomAtivo)
		: null;
	const fixedValueOriginal = Number(fixedValue) || 0;
	// Números de exibição do cupom (desconto no fixo, âncora riscada e "você
	// economizou") — conta única em utils/cupom (resumoExibicaoCupom), espelhando
	// o descontoCupomDoPlano do back. Regressão do ELEVAR_NIVEL_2026: o
	// valor_desconto de cupom com plano-base vinha na base do outro plano e a
	// tela subtraía do fixo do alvo (mostrava 432,38, cobrava 791,28).
	const {
		descontoFixo,
		ancoraFixo,
		economiaCupom,
	} = cupomAplicado
		? resumoExibicaoCupom(planoParaCupom, cupomAtivo, fixedValueOriginal)
		: {
				descontoFixo: 0,
				ancoraFixo: fixedValueOriginal,
				economiaCupom: 0,
		  };
	const creditoAbatidoNum = Math.max(0, Number(creditoAbatido) || 0);
	// Crédito da conta: aplica só o que sobra depois de cupom + resíduo (o back
	// respeita o piso do Asaas; no anual nunca chega perto, então min basta).
	const aposCupomAbatimento = Math.max(
		0,
		fixedValueOriginal - descontoFixo - creditoAbatidoNum,
	);
	const creditoContaAplicado = Math.min(
		Math.max(0, Number(creditoConta) || 0),
		aposCupomAbatimento,
	);
	const fixedValueEfetivo = Math.max(
		0,
		fixedValueOriginal -
			descontoFixo -
			creditoAbatidoNum -
			creditoContaAplicado,
	);
	const totalValueEfetivo = Math.max(
		0,
		(Number(totalValue) || 0) -
			descontoFixo -
			creditoAbatidoNum -
			creditoContaAplicado,
	);

	useEffect(() => {
		if (paymentMethod !== "CREDIT_CARD") {
			setInstallments(1);
			return;
		}
		const max = Math.max(1, maxInstallments || 1);
		setInstallments((prev) => Math.min(Math.max(prev || 1, 1), max));
	}, [paymentMethod, maxInstallments]);

	const parcelasInfo = useMemo(
		() =>
			calcularTotalComJuros(
				fixedValueEfetivo,
				variableMonthlyValue,
				installments,
				taxasParcelamentoCartao,
				variavelNoTotal,
			),
		[
			fixedValueEfetivo,
			variableMonthlyValue,
			installments,
			taxasParcelamentoCartao,
			variavelNoTotal,
		],
	);
	const mostraJuros = paymentMethod === "CREDIT_CARD" && installments > 1;
	const fixedValuePeriodLabel = useMemo(() => {
		switch (maxInstallments) {
			case 1:
				return "/mês";
			case 3:
				return "/trimestre";
			case 6:
				return "/semestre";
			case 12:
				return "/ano";
			default:
				return "";
		}
	}, [maxInstallments]);

	const canSubmit = useMemo(() => {
		// Se já gerou QR Code do PIX, não permite submeter novamente
		if (result?.status === "PIX") return false;
		if (paymentMethod === "CREDIT_CARD") {
			// Cartão salvo selecionado: nada a preencher — só confirmar.
			if (cartaoSalvo && usarCartaoSalvo) return true;
			const { month, year } = parseExpiry(cardExpiry);
			const cpfCnpjDigits = onlyDigits(holderCpfCnpj);
			const postalDigits = onlyDigits(holderPostalCode);
			const phoneDigits = onlyDigits(holderPhone);
			return (
				onlyDigits(cardNumber).length >= 13 &&
				month &&
				year &&
				onlyDigits(cardCvc).length >= 3 &&
				cardHolderName.trim().length > 2 &&
				holderEmail.trim().length > 3 &&
				(cpfCnpjDigits.length === 11 ||
					cpfCnpjDigits.length === 14) &&
				postalDigits.length === 8 &&
				holderAddressNumber.trim().length > 0 &&
				phoneDigits.length >= 10
			);
		}
		if (paymentMethod === "PIX") {
			// CEP + número também no PIX: o cliente Asaas nasce dos dados da
			// conta e sem endereço a NFS-e é recusada (o Asaas enriquece
			// rua/bairro/cidade a partir do CEP).
			return (
				onlyDigits(cpfPix).length === 11 &&
				onlyDigits(holderPostalCode).length === 8 &&
				holderAddressNumber.trim().length > 0
			);
		}
		return false;
	}, [
		paymentMethod,
		cardNumber,
		cardExpiry,
		cardCvc,
		cardHolderName,
		holderEmail,
		holderCpfCnpj,
		holderPostalCode,
		holderAddressNumber,
		holderPhone,
		cpfPix,
		result?.status,
		cartaoSalvo,
		usarCartaoSalvo,
	]);

	const handleSubmit = () => {
		if (!onSubmit) return;

		const base = {
			paymentMethod,
			...(consentimentoFlipVersao
				? { consentimentoFlipVersao }
				: {}),
			// Cupom no payload (contrato): id do cupom ativo aplicado a este
			// plano. Ausente/null quando não há cupom aplicável.
			...(cupomAplicado && cupomAtivo?.id
				? { cupomId: cupomAtivo.id }
				: {}),
		};

		if (paymentMethod === "CREDIT_CARD") {
			// Cartão salvo: o back cobra pelo token — sem dados de cartão.
			if (cartaoSalvo && usarCartaoSalvo) {
				onSubmit({
					...base,
					installmentCount: installments,
					usarCartaoSalvo: true,
				});
				return;
			}
			const { month, year } = parseExpiry(cardExpiry);
			const creditCardHolderInfo = {
				name: cardHolderName.trim(),
				email: holderEmail.trim(),
				cpfCnpj: onlyDigits(holderCpfCnpj),
				postalCode: onlyDigits(holderPostalCode),
				addressNumber: holderAddressNumber.trim(),
				addressComplement: holderAddressComplement.trim() || undefined,
				phone: onlyDigits(holderPhone),
				mobilePhone: holderMobilePhone.trim()
					? onlyDigits(holderMobilePhone)
					: undefined,
			};
			onSubmit({
				...base,
				installmentCount: installments,
				saveCard: !!saveCard,
				creditCard: {
					holderName: cardHolderName.trim(),
					number: onlyDigits(cardNumber),
					expiryMonth: month,
					expiryYear: year,
					ccv: onlyDigits(cardCvc),
				},
				creditCardHolderInfo,
			});
			return;
		}

		onSubmit({
			...base,
			cpf: onlyDigits(cpfPix),
			// Dados do pagador no PIX (contrato novo): os consumidores
			// persistem na conta ANTES de cobrar — o cliente Asaas é montado
			// com usuario.*, e sem CEP a NFS-e é recusada.
			pagador: {
				cpfCnpj: onlyDigits(cpfPix),
				postalCode: onlyDigits(holderPostalCode),
				addressNumber: holderAddressNumber.trim(),
				addressComplement:
					holderAddressComplement.trim() || undefined,
			},
		});
	};

	// Campo de cupom no resumo (só quando permiteCupom). Dois estados: aplicado
	// (mostra "Cupom X aplicado (−Y%)" + remover) ou em branco (input + Aplicar).
	const cupomBox = permiteCupom ? (
		cupomAplicado ? (
			<div
				style={{
					background: "#f6ffed",
					border: "1px solid #b7eb8f",
					borderRadius: 10,
					padding: "8px 11px",
					marginBottom: 14,
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					gap: 8,
				}}
			>
				<span
					style={{
						color: "#389e0d",
						fontWeight: 600,
						fontSize: 13,
					}}
				>
					✓ Cupom <b>{cupomAtivo?.codigo}</b> aplicado (−
					{formatarPorcentagemCupom(detalheCupom?.percentual)})
				</span>
				{onRemoverCupom ? (
					<Button
						type="link"
						size="small"
						onClick={onRemoverCupom}
						style={{
							padding: 0,
							height: "auto",
							color: "rgba(0,0,0,.4)",
						}}
					>
						remover
					</Button>
				) : null}
			</div>
		) : (
			<div
				style={{
					background: "#fff",
					border: "1px dashed #ff7a18",
					borderRadius: 10,
					padding: 12,
					marginBottom: 14,
				}}
			>
				<div
					style={{
						fontSize: 13,
						fontWeight: 600,
						marginBottom: 7,
						color: "rgba(0,0,0,.85)",
					}}
				>
					Tem um cupom de desconto?
				</div>
				<Input.Group compact style={{ display: "flex" }}>
					<Input
						style={{ flex: 1 }}
						placeholder="Digite o código"
						value={cupomCodeInput}
						onChange={(e) => setCupomCodeInput(e.target.value)}
						onPressEnter={() => {
							const codigo = (cupomCodeInput || "").trim();
							if (codigo && onAplicarCupom) onAplicarCupom(codigo);
						}}
						disabled={aplicandoCupom}
					/>
					<Button
						type="primary"
						loading={aplicandoCupom}
						onClick={() => {
							const codigo = (cupomCodeInput || "").trim();
							if (codigo && onAplicarCupom) onAplicarCupom(codigo);
						}}
						style={{
							background: "#ff7a18",
							borderColor: "#ff7a18",
						}}
					>
						Aplicar
					</Button>
				</Input.Group>
			</div>
		)
	) : null;

	const resumoPedido = (
		<Card
			style={{
				borderRadius: 12,
				background: "#f2f2f2",
				color: "#1f1f1f",
			}}
			bodyStyle={{ padding: isMobile ? 14 : 20 }}
		>
			<div
				style={{
					color: "rgba(0,0,0,.6)",
					marginBottom: 6,
				}}
			>
				Resumo do pedido
			</div>
			<div
				style={{
					fontSize: isMobile ? 18 : 22,
					fontWeight: 700,
					marginBottom: isMobile ? 8 : 12,
				}}
			>
				{planName}
			</div>

			{cupomBox}

			{features?.length > 0 ? (
				<div style={{ marginBottom: 16 }}>
					{features.map((f, idx) => (
						<div
							key={idx}
							style={{
								display: "flex",
								gap: 8,
								marginBottom: 8,
							}}
						>
							<div
								style={{
									color: "#ff7a18",
									fontWeight: 700,
								}}
							>
								✓
							</div>
							<div
								style={{
									color: "rgba(0,0,0,.85)",
								}}
							>
								{f}
							</div>
						</div>
					))}
				</div>
			) : null}

			<div
				style={{
					borderTop: "1px solid rgba(255,255,255,.08)",
					paddingTop: 12,
					marginTop: 12,
				}}
			>
				{!hideFixedValueLine && (
					<div style={{ marginBottom: 8 }}>
						<Text style={{ color: "rgba(0,0,0,.6)" }}>
							{cobrancaUnica ? "Valor a pagar" : "Valor fixo"}
						</Text>
						<div style={{ fontWeight: 700 }}>
							{cupomAplicado ||
						creditoAbatidoNum > 0 ||
						creditoContaAplicado > 0 ? (
								<span
									style={{
										textDecoration: "line-through",
										color: "rgba(0,0,0,.4)",
										fontWeight: 400,
										fontSize: 14,
										marginRight: 6,
									}}
								>
									{formatCurrency(ancoraFixo)}
								</span>
							) : null}
							<span
								style={
									cupomAplicado
										? { color: "#389e0d" }
										: undefined
								}
							>
								{formatCurrency(fixedValueEfetivo)}
							</span>
							{!cobrancaUnica && fixedValuePeriodLabel
								? ` ${fixedValuePeriodLabel}`
								: ""}
						</div>
					</div>
				)}

				{creditoAbatidoNum > 0 && (
					<div style={{ marginBottom: 8 }}>
						<Text style={{ color: "rgba(0,0,0,.6)" }}>
							Abatimento do mês atual
						</Text>
						<div style={{ fontWeight: 700, color: "#389e0d" }}>
							− {formatCurrency(creditoAbatidoNum)}
						</div>
					</div>
				)}

				{creditoContaAplicado > 0 && (
					<div style={{ marginBottom: 8 }}>
						<Text style={{ color: "rgba(0,0,0,.6)" }}>
							Crédito disponível
						</Text>
						<div style={{ fontWeight: 700, color: "#389e0d" }}>
							− {formatCurrency(creditoContaAplicado)}
						</div>
					</div>
				)}

				{!cobrancaUnica && Number(variableMonthlyValue) > 0 && (
					<div style={{ marginBottom: 8 }}>
						<Text style={{ color: "rgba(0,0,0,.6)" }}>
							Valor variável (sempre mensal)
						</Text>
						<div style={{ fontWeight: 700 }}>
							{formatCurrency(variableMonthlyValue)}
							{studentsCount >= 1
								? ` (${studentsCount} aluno${
										studentsCount === 1 ? "" : "s"
								  })`
								: ""}{" "}
							/mês
						</div>
					</div>
				)}

				<div style={{ marginBottom: 8 }}>
					<Text style={{ color: "rgba(0,0,0,.6)" }}>Total</Text>
					<div
						style={{
							fontSize: isMobile ? 20 : 24,
							fontWeight: 800,
							...(cupomAplicado ? { color: "#c2410c" } : {}),
						}}
					>
						{formatCurrency(totalValueEfetivo)}
					</div>
					{cupomAplicado && economiaCupom > 0 ? (
						<div
							style={{
								fontSize: 12,
								color: "#389e0d",
								fontWeight: 600,
								marginTop: 2,
							}}
						>
							Você economizou {formatCurrency(economiaCupom)}
						</div>
					) : null}
				</div>

				{mostraJuros && (
					<>
						<div style={{ marginBottom: 8 }}>
							<Text style={{ color: "rgba(0,0,0,.6)" }}>
								Parcelamento (cartão)
							</Text>
							<div
								style={{
									fontSize: isMobile ? 20 : 24,
									fontWeight: 800,
									color: "#c05a10",
								}}
							>
								{installments}x de{" "}
								{formatCurrency(parcelasInfo.valorParcela)}
							</div>
						</div>

						<div
							style={{
								background: "#fff",
								borderRadius: 8,
								padding: "10px 12px",
								marginBottom: 8,
							}}
						>
							<div
								style={{
									display: "flex",
									justifyContent: "space-between",
									marginBottom: 4,
								}}
							>
								<Text style={{ color: "rgba(0,0,0,.6)" }}>
									Valor à vista
								</Text>
								<Text>{formatCurrency(totalValueEfetivo)}</Text>
							</div>
							<div
								style={{
									display: "flex",
									justifyContent: "space-between",
									marginBottom: 4,
								}}
							>
								<Text style={{ color: "rgba(0,0,0,.6)" }}>
									Juros do parcelamento
								</Text>
								<Text>
									+ {formatCurrency(parcelasInfo.juros)}
								</Text>
							</div>
							<div
								style={{
									display: "flex",
									justifyContent: "space-between",
									fontWeight: 700,
								}}
							>
								<Text strong>Total parcelado</Text>
								<Text strong>
									{formatCurrency(parcelasInfo.total)}
								</Text>
							</div>
						</div>

						<div
							style={{
								fontSize: 12,
								color: "rgba(0,0,0,.45)",
								marginBottom: 8,
							}}
						>
							À vista (1x) ou PIX:{" "}
							{formatCurrency(totalValueEfetivo)}, sem juros.
						</div>
					</>
				)}
			</div>

			<Button
				type="primary"
				size="large"
				block
				onClick={handleSubmit}
				disabled={!canSubmit}
				loading={loading}
				style={{
					marginTop: isMobile ? 12 : 16,
					background: "#ff7a18",
					borderColor: "#ff7a18",
					height: isMobile ? 44 : 48,
					fontWeight: 700,
				}}
			>
				{submitLabel}
			</Button>

			{result?.status === "PAID" ? (
				<div style={{ marginTop: 16, color: "#389e0d" }}>
					Pagamento confirmado com sucesso.
				</div>
			) : null}

			{result?.status === "PIX" && result?.pix ? (
				<div style={{ marginTop: 16 }}>
					<div
						style={{
							color: "rgba(0,0,0,.75)",
							marginBottom: 8,
						}}
					>
						Pague com PIX
					</div>
					{result.pix.encodedImage ? (
						<div
							style={{
								background: "#fff",
								padding: 12,
								borderRadius: 8,
							}}
						>
							<img
								src={`data:image/png;base64,${result.pix.encodedImage}`}
								alt="QR Code PIX"
								style={{
									width: "100%",
									height: "auto",
								}}
							/>
						</div>
					) : null}
					{result.pix.payload ? (
						<div style={{ marginTop: 10 }}>
							<div
								style={{
									display: "flex",
									alignItems: "center",
									gap: 8,
								}}
							>
								<Text style={{ color: "rgba(0,0,0,.75)" }}>
									Copia e cola:
								</Text>
								<Button
									type="text"
									size="small"
									icon={<CopyOutlined />}
									onClick={async () => {
										try {
											await navigator.clipboard.writeText(
												result.pix.payload,
											);
											message.success(
												"Código PIX copiado",
											);
										} catch (err) {
											message.error(
												"Não foi possível copiar o código PIX",
											);
										}
									}}
								/>
							</div>
							<Input.TextArea
								value={result.pix.payload}
								readOnly
								autoSize={{ minRows: 3, maxRows: 6 }}
								style={{ marginTop: 6 }}
							/>
						</div>
					) : null}
				</div>
			) : null}
		</Card>
	);

	return (
		<Row gutter={[24, 24]}>
			<Col xs={24} lg={14}>
				<div style={{ marginBottom: 24 }}>
					<Title level={4} style={{ marginBottom: 12 }}>
						1. Pagamento
					</Title>

					<Radio.Group
						value={paymentMethod}
						onChange={(e) => setPaymentMethod(e.target.value)}
						style={{ width: "100%" }}
					>
						<Row gutter={[isMobile ? 8 : 12, isMobile ? 8 : 12]}>
							<Col xs={24} md={12}>
								<Card
									onClick={() =>
										setPaymentMethod("CREDIT_CARD")
									}
									hoverable
									style={{
										borderColor:
											paymentMethod === "CREDIT_CARD"
												? "#ff7a18"
												: "#f0f0f0",
									}}
									bodyStyle={{ padding: 16 }}
								>
									<Radio value="CREDIT_CARD">
										Cartão de Crédito
									</Radio>
								</Card>
							</Col>
							<Col xs={24} md={12}>
								<Card
									onClick={() => setPaymentMethod("PIX")}
									hoverable
									style={{
										borderColor:
											paymentMethod === "PIX"
												? "#ff7a18"
												: "#f0f0f0",
									}}
									bodyStyle={{ padding: 16 }}
								>
									<Radio value="PIX">PIX</Radio>
								</Card>
							</Col>
						</Row>
					</Radio.Group>

					<div style={{ marginTop: 16 }}>
						{paymentMethod === "CREDIT_CARD" &&
						cartaoSalvo &&
						usarCartaoSalvo ? (
							<Card style={{ borderRadius: 8 }}>
								<Title level={5} style={{ marginBottom: 4 }}>
									Pagamento
								</Title>
								<Text type="secondary">
									A cobrança será feita no seu cartão salvo.
								</Text>
								<div
									style={{
										marginTop: 16,
										border: "1px solid #f0f0f0",
										borderRadius: 10,
										padding: "12px 14px",
										display: "flex",
										alignItems: "center",
										justifyContent: "space-between",
										gap: 8,
										flexWrap: "wrap",
									}}
								>
									<div>
										<Text strong style={{ fontSize: 15 }}>
											{cartaoSalvo.ultimos_digitos
												? `•••• •••• •••• ${cartaoSalvo.ultimos_digitos}`
												: "Cartão salvo"}
										</Text>
										{cartaoSalvo.bandeira && (
											<Tag style={{ marginLeft: 8 }}>
												{cartaoSalvo.bandeira}
											</Tag>
										)}
										{cartaoSalvo.titular && (
											<div style={{ marginTop: 2 }}>
												<Text type="secondary">
													{cartaoSalvo.titular}
												</Text>
											</div>
										)}
									</div>
									<Button
										type="link"
										style={{ paddingRight: 0 }}
										onClick={() =>
											setUsarCartaoSalvo(false)
										}
									>
										Usar outro cartão
									</Button>
								</div>

								{maxInstallments > 1 && (
									<div style={{ marginTop: 12 }}>
										<RotuloCampo>Parcelamento</RotuloCampo>
										<Select
											value={installments}
											onChange={setInstallments}
											style={{ width: "100%" }}
											options={Array.from(
												{
													length: Math.max(
														1,
														maxInstallments,
													),
												},
												(_v, idx) => {
													const n = idx + 1;
													const info =
														calcularTotalComJuros(
															fixedValueEfetivo,
															variableMonthlyValue,
															n,
															taxasParcelamentoCartao,
															variavelNoTotal,
														);
													return {
														value: n,
														label:
															n === 1
																? `1x de ${formatCurrency(
																		info.total,
																  )} · à vista`
																: `${n}x de ${formatCurrency(
																		info.valorParcela,
																  )} · total ${formatCurrency(
																		info.total,
																  )}`,
													};
												},
											)}
										/>
									</div>
								)}
							</Card>
						) : paymentMethod === "CREDIT_CARD" ? (
							<Card style={{ borderRadius: 8 }}>
								<Title level={5} style={{ marginBottom: 4 }}>
									Dados do cartão
								</Title>
								<Text type="secondary">
									Pagamento seguro via ASAAS
								</Text>
								{cartaoSalvo && (
									<div style={{ marginTop: 8 }}>
										<Button
											type="link"
											style={{ paddingLeft: 0 }}
											onClick={() =>
												setUsarCartaoSalvo(true)
											}
										>
											← Usar o cartão salvo
											{cartaoSalvo.ultimos_digitos
												? ` (•••• ${cartaoSalvo.ultimos_digitos})`
												: ""}
										</Button>
									</div>
								)}

								<div style={{ marginTop: 16 }}>
									<RotuloCampo obrigatorio>Número do cartão</RotuloCampo>
									<Input
										value={cardNumber}
										onChange={(e) =>
											setCardNumber(
												formatCardNumber(
													e.target.value,
												),
											)
										}
										placeholder="0000 0000 0000 0000"
										size="large"
										inputMode="numeric"
									/>
								</div>

								<Row
									gutter={[12, 12]}
									style={{ marginTop: 12 }}
								>
									<Col xs={24} md={12}>
										<RotuloCampo obrigatorio>Validade</RotuloCampo>
										<Input
											value={cardExpiry}
											onChange={(e) =>
												setCardExpiry(
													formatExpiry(
														e.target.value,
													),
												)
											}
											placeholder="MM/AA"
											size="large"
											inputMode="numeric"
										/>
									</Col>
									<Col xs={24} md={12}>
										<RotuloCampo obrigatorio>CVC</RotuloCampo>
										<Input
											value={cardCvc}
											onChange={(e) =>
												setCardCvc(
													onlyDigits(
														e.target.value,
													).slice(0, 4),
												)
											}
											placeholder="123"
											size="large"
											inputMode="numeric"
										/>
									</Col>
								</Row>

								<div style={{ marginTop: 12 }}>
									<RotuloCampo obrigatorio>Nome no cartão</RotuloCampo>
									<Input
										value={cardHolderName}
										onChange={(e) =>
											setCardHolderName(e.target.value)
										}
										placeholder="COMO NO CARTÃO"
										size="large"
									/>
								</div>

								<div style={{ marginTop: 12 }}>
									<RotuloCampo obrigatorio>E-mail do titular</RotuloCampo>
									<Input
										value={holderEmail}
										onChange={(e) =>
											setHolderEmail(e.target.value)
										}
										placeholder="titular@email.com"
										size="large"
										type="email"
									/>
								</div>

								<div style={{ marginTop: 12 }}>
									<RotuloCampo obrigatorio>CPF ou CNPJ do titular</RotuloCampo>
									<InputCpfCnpj
										value={holderCpfCnpj}
										onChange={(value) =>
											setHolderCpfCnpj(
												onlyDigits(value).slice(
													0,
													14,
												),
											)
										}
										placeholder="Somente números"
										size="large"
										inputMode="numeric"
									/>
								</div>

								<Row gutter={[12, 12]} style={{ marginTop: 12 }}>
									<Col xs={24} md={12}>
										<RotuloCampo obrigatorio>CEP</RotuloCampo>
										<InputCep
											value={holderPostalCode}
											onChange={(e) =>
												setHolderPostalCode(
													e.target.value,
												)
											}
											size="large"
											inputMode="numeric"
										/>
									</Col>
									<Col xs={24} md={12}>
										<RotuloCampo obrigatorio>Número do endereço</RotuloCampo>
										<Input
											value={holderAddressNumber}
											onChange={(e) =>
												setHolderAddressNumber(
													e.target.value,
												)
											}
											placeholder="Número"
											size="large"
										/>
									</Col>
								</Row>

								<div style={{ marginTop: 12 }}>
									<RotuloCampo>Complemento</RotuloCampo>
									<Input
										value={holderAddressComplement}
										onChange={(e) =>
											setHolderAddressComplement(
												e.target.value,
											)
										}
										placeholder="Apartamento, bloco, etc."
										size="large"
									/>
								</div>

								<Row gutter={[12, 12]} style={{ marginTop: 12 }}>
									<Col xs={24} md={12}>
										<RotuloCampo obrigatorio>Telefone</RotuloCampo>
										{telefoneInternacional ? (
											<InputTelefoneInternacional
												value={holderPhone}
												onChange={(e) =>
													setHolderPhone(e?.target?.value || "")
												}
												placeholder="Telefone"
												defaultCountry="br"
											/>
										) : (
											<Input
												value={holderPhone}
												onChange={(e) =>
													setHolderPhone(
														onlyDigits(
															e.target.value,
														).slice(0, 11),
													)
												}
												placeholder="DDD + telefone"
												size="large"
												inputMode="numeric"
											/>
										)}
									</Col>
									<Col xs={24} md={12}>
										<RotuloCampo>Celular</RotuloCampo>
										{telefoneInternacional ? (
											<InputTelefoneInternacional
												value={holderMobilePhone}
												onChange={(e) =>
													setHolderMobilePhone(e?.target?.value || "")
												}
												placeholder="Celular"
												defaultCountry="br"
											/>
										) : (
											<Input
												value={holderMobilePhone}
												onChange={(e) =>
													setHolderMobilePhone(
														onlyDigits(
															e.target.value,
														).slice(0, 11),
													)
												}
												placeholder="DDD + celular"
												size="large"
												inputMode="numeric"
											/>
										)}
									</Col>
								</Row>

								{maxInstallments > 1 && (
									<div style={{ marginTop: 12 }}>
										<RotuloCampo>Parcelamento</RotuloCampo>
										<Select
											value={installments}
											onChange={setInstallments}
											style={{ width: "100%" }}
											options={Array.from(
												{
													length: Math.max(
														1,
														maxInstallments,
													),
												},
												(_v, idx) => {
													const n = idx + 1;
													const info =
														calcularTotalComJuros(
															fixedValueEfetivo,
															variableMonthlyValue,
															n,
															taxasParcelamentoCartao,
															variavelNoTotal,
														);
													return {
														value: n,
														label:
															n === 1
																? `1x de ${formatCurrency(
																		info.total,
																  )} · à vista`
																: `${n}x de ${formatCurrency(
																		info.valorParcela,
																  )} · total ${formatCurrency(
																		info.total,
																  )}`,
													};
												},
											)}
										/>
									</div>
								)}

								<div style={{ marginTop: 12 }}>
									<Checkbox
										checked={saveCard}
										onChange={(e) =>
											setSaveCard(e.target.checked)
										}
									>
										Usar esse cartão para pagar
										automaticamente as próximas faturas
									</Checkbox>
								</div>
							</Card>
						) : (
							<Card style={{ borderRadius: 8 }}>
								<Title level={5} style={{ marginBottom: 4 }}>
									Dados do PIX
								</Title>
								<Text type="secondary">
									Você verá o QR Code após confirmar
								</Text>

								<div style={{ marginTop: 16 }}>
									<RotuloCampo obrigatorio>CPF</RotuloCampo>
									<InputCpf
										value={cpfPix}
										onChange={(value) =>
											setCpfPix(
												onlyDigits(value).slice(0, 11),
											)
										}
									/>
								</div>

								{/* Endereço também no PIX (mesmos estados do
								    cartão — trocar de método preserva): o
								    cliente Asaas nasce dos dados da conta e
								    sem CEP a NFS-e é recusada. */}
								<Row
									gutter={[12, 12]}
									style={{ marginTop: 12 }}
								>
									<Col xs={24} md={12}>
										<RotuloCampo obrigatorio>CEP</RotuloCampo>
										<InputCep
											value={holderPostalCode}
											onChange={(e) =>
												setHolderPostalCode(
													e.target.value,
												)
											}
											size="large"
											inputMode="numeric"
										/>
									</Col>
									<Col xs={24} md={12}>
										<RotuloCampo obrigatorio>Número do endereço</RotuloCampo>
										<Input
											value={holderAddressNumber}
											onChange={(e) =>
												setHolderAddressNumber(
													e.target.value,
												)
											}
											placeholder="Número"
											size="large"
										/>
									</Col>
								</Row>

								<div style={{ marginTop: 12 }}>
									<RotuloCampo>Complemento</RotuloCampo>
									<Input
										value={holderAddressComplement}
										onChange={(e) =>
											setHolderAddressComplement(
												e.target.value,
											)
										}
										placeholder="Apartamento, bloco, etc. (opcional)"
										size="large"
									/>
								</div>
							</Card>
						)}
					</div>
				</div>

				{errorMessage ? (
					<Alert
						type="error"
						showIcon
						message="Não foi possível processar o pagamento"
						description={errorMessage}
						style={{ marginTop: 12 }}
					/>
				) : null}
			</Col>

			{isMobile ? (
				<Col xs={24} style={{ marginTop: 8 }}>
					{resumoPedido}
				</Col>
			) : (
				<Col xs={24} lg={10}>
					{resumoPedido}
				</Col>
			)}
		</Row>
	);
}
